import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Download, Calendar, TrendingUp, Users, 
  Award, Clock, BookOpen, BarChart3 
} from 'lucide-react';
import api from '../../services/api';
import Navbar from '../../components/Navbar';
import Loading from '../../components/Loading';
import toast from 'react-hot-toast';
import { 
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  PieChart, Pie, Cell, Legend
} from 'recharts';

const Analizler = () => {
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30');
  const [analytics, setAnalizler] = useState(null);

  useEffect(() => {
    fetchAnalizler();
  }, [timeRange]);

  const fetchAnalizler = async () => {
    try {
      const response = await api.get(`/admin/analytics?days=${timeRange}`);
      setAnalizler(response.data);
    } catch (err) {
      toast.error('Analizler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type) => {
    try {
      const response = await api.get(`/admin/export/${type}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success(`${type} başarıyla dışa aktarıldı`);
    } catch (err) {
      toast.error('Veri dışa aktarılamadı');
    }
  };

  if (loading) return <Loading />;

  const COLORS = ['#C62828', '#059669', '#d97706', '#dc2626', '#7c3aed'];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
      <Navbar />
      
      <div className="container py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">Analizler</h1>
            <p className="text-gray">Platform kullanım ve performans analizleri</p>
          </div>
          <div className="flex items-center gap-3">
            <select 
              value={timeRange} 
              onChange={(e) => setTimeRange(e.target.value)}
              className="form-select"
              style={{ width: 'auto' }}
            >
              <option value="7">Son 7 Gün</option>
              <option value="30">Son 30 Gün</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <div className="dropdown">
              <button className="btn btn-primary">
                <Download size={18} /> Dışa Aktar
              </button>
              <div className="dropdown-menu" style={{ display: 'none' }}>
                <button onClick={() => handleExport('users')} className="dropdown-item">
                  Kullanıcıları Dışa Aktar
                </button>
                <button onClick={() => handleExport('progress')} className="dropdown-item">
                  İlerleme Verilerini Dışa Aktar
                </button>
                <button onClick={() => handleExport('certificates')} className="dropdown-item">
                  Sertifikaları Dışa Aktar
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid mb-6" style={{ gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
          <SummaryCard 
            icon={<Users size={20} />}
            label="Toplam Kullanıcı"
            value={analytics?.summary?.totalUsers || 0}
            change={`+${analytics?.summary?.newUsers || 0}`}
            color="#C62828"
          />
          <SummaryCard 
            icon={<BookOpen size={20} />}
            label="Kayıtlar"
            value={analytics?.summary?.totalEnrollments || 0}
            change={`+${analytics?.summary?.newEnrollments || 0}`}
            color="#059669"
          />
          <SummaryCard 
            icon={<Award size={20} />}
            label="Sertifikalar"
            value={analytics?.summary?.totalCertificates || 0}
            change={`+${analytics?.summary?.newCertificates || 0}`}
            color="#d97706"
          />
          <SummaryCard 
            icon={<TrendingUp size={20} />}
            label="Tamamlanma Oranı"
            value={`${analytics?.summary?.completionRate || 0}%`}
            change={`${analytics?.summary?.completionRateChange > 0 ? '+' : ''}${analytics?.summary?.completionRateChange || 0}%`}
            color="#7c3aed"
          />
          <SummaryCard 
            icon={<Clock size={20} />}
            label="Ortalama Süre"
            value={`${analytics?.summary?.avgTimeSpent || 0}m`}
            change="kullanıcı başına"
            color="#dc2626"
          />
        </div>

        {/* Charts Row 1 */}
        <div className="grid mb-6" style={{ gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
          {/* User Activity */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">Kullanıcı Aktivite Grafiği</h2>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analytics?.userActivity || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip />
                  <Area type="monotone" dataKey="activeUsers" stroke="#C62828" fill="#e0f2fe" name="Aktif Kullanıcılar" />
                  <Area type="monotone" dataKey="newUsers" stroke="#059669" fill="#d1fae5" name="Yeni Kullanıcılar" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Status Distribution */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">Kullanıcı Durumu</h2>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={analytics?.statusDistribution || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    labelLine={false}
                  >
                    {(analytics?.statusDistribution || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 mt-2">
                {(analytics?.statusDistribution || []).map((item, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: COLORS[index % COLORS.length] }}></div>
                    <span className="text-xs text-gray">{item.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid mb-6" style={{ gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* Completion Trends */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">Zaman İçinde Tamamlamalar</h2>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={analytics?.completionTrend || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="date" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip />
                  <Line type="monotone" dataKey="completions" stroke="#059669" strokeWidth={2} name="Tamamlamalar" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Puan Dağılımı */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">Puan Dağılımı</h2>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={analytics?.scoreDistribution || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="range" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#C62828" name="Kullanıcılar" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Tables Row */}
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
          {/* En Başarılı Kullanıcılar */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">En Başarılı Kullanıcılar</h2>
              <Link to="/admin/users" className="text-sm text-primary">Tümünü Gör</Link>
            </div>
            <div className="card-body p-0">
              <table className="table">
                <thead>
                  <tr>
                    <th>Sıra</th>
                    <th>Kullanıcı</th>
                    <th>Puan</th>
                    <th>Süre</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.topPerformers || []).slice(0, 10).map((user, index) => (
                    <tr key={user.id}>
                      <td>
                        <span className={`badge ${index < 3 ? 'badge-warning' : 'badge-gray'}`}>
                          #{index + 1}
                        </span>
                      </td>
                      <td>
                        <Link to={`/admin/users/${user.id}`} className="font-medium">
                          {user.name}
                        </Link>
                      </td>
                      <td>
                        <span className="badge badge-success">{user.score}%</span>
                      </td>
                      <td className="text-gray">{formatTime(user.timeSpent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!analytics?.topPerformers || analytics.topPerformers.length === 0) && (
                <div className="p-6 text-center text-gray">Veri bulunamadı</div>
              )}
            </div>
          </div>

          {/* Son Sertifikalar */}
          <div className="card">
            <div className="card-header">
              <h2 className="text-lg font-semibold">Son Sertifikalar</h2>
            </div>
            <div className="card-body p-0">
              <table className="table">
                <thead>
                  <tr>
                    <th>Kullanıcı</th>
                    <th>Eğitim</th>
                    <th>Tarih</th>
                    <th>Puan</th>
                  </tr>
                </thead>
                <tbody>
                  {(analytics?.recentSertifikalar || []).slice(0, 10).map((cert, index) => (
                    <tr key={index}>
                      <td className="font-medium">{cert.userName}</td>
                      <td className="text-gray">{cert.courseTitle}</td>
                      <td className="text-gray text-sm">
                        {new Date(cert.issuedAt).toLocaleDateString()}
                      </td>
                      <td>
                        <span className="badge badge-success">{cert.score}%</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(!analytics?.recentSertifikalar || analytics.recentSertifikalar.length === 0) && (
                <div className="p-6 text-center text-gray">Henüz sertifika oluşturulmamış</div>
              )}
            </div>
          </div>
        </div>

        {/* School Performance */}
        <div className="card mt-6">
          <div className="card-header">
            <h2 className="text-lg font-semibold">Kurum Bazlı Performans</h2>
          </div>
          <div className="card-body">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={analytics?.schoolPerformance || []} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis type="number" stroke="#94a3b8" fontSize={12} />
                <YAxis type="category" dataKey="school" stroke="#94a3b8" fontSize={12} width={150} />
                <Tooltip />
                <Bar dataKey="users" fill="#C62828" name="Kullanıcılar" radius={[0, 4, 4, 0]} />
                <Bar dataKey="completions" fill="#059669" name="Tamamlamalar" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ icon, label, value, change, color }) => (
  <div className="card">
    <div className="card-body p-4">
      <div className="flex items-center gap-3">
        <div style={{ color }}>{icon}</div>
        <div className="flex-1">
          <p className="text-xs text-gray">{label}</p>
          <p className="text-xl font-bold">{value}</p>
        </div>
        <span className="text-xs text-gray">{change}</span>
      </div>
    </div>
  </div>
);

const formatTime = (seconds) => {
  if (!seconds) return '0m';
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

export default Analizler;
